/**
 * SpotCard — state owner + visual-layout dispatcher.
 *
 * Two visual layouts share this container so the report flow, sizing
 * state, callLabel derivation, and `isFacingBet` export live in one
 * place regardless of which view the user has selected in Settings:
 *
 *   - Table (default): PokerTable oval + TableActionBar + question
 *     caption above + per-street HistoryNote below.
 *   - Simplified: a structured card (`SimplifiedSpotCard`) that
 *     encapsulates everything inside a single rounded surface —
 *     top bar, pot, board, hero hand, multi-street history, question,
 *     and the TableActionBar slot. Opt-in via Settings → Display.
 *
 * The preference comes from `useSpotLayout()` (localStorage-backed),
 * default `"table"`.
 *
 * Public API unchanged so MessageBubble doesn't know which view is rendering:
 *   - props: `spot`, `locked`, `onAction`, `conversationId?`, `onNextSpot?`
 *   - exports `isFacingBet` (test-pinned regression: BTN raises + SB
 *     folds → BB still facing the BTN raise).
 */

import { useState, type ReactNode } from "react";

import { Flag, ICON_STROKE, X } from "@/shared/components/icons";
import { reportLibrarySpot } from "@/shared/api";
import { formatBb } from "@/shared/format";
import { historyToSentences, type StreetNarrative } from "@/features/chat/lib/handHistoryNL";
import type { SpotAction, SpotPayload } from "@/shared/models";
import { useSpotLayout } from "@/shared/preferences";
import { PokerTable } from "@/features/chat/components/PokerTable";
import { SimplifiedSpotCard } from "@/features/chat/components/SimplifiedSpotCard";
import { TableActionBar, callAmount } from "@/features/chat/components/TableActionBar";

interface SpotCardProps {
  spot: SpotPayload;
  locked: boolean;
  onAction: (action: SpotAction) => void;
  /** Conversation id — required for the report flow. Absent only on
   *  brand-new conversations between the meta event and first SSE
   *  chunk; we hide the report button in that window. */
  conversationId?: string;
  /** Fired when the user clicks "Next spot" after reporting this spot.
   *  Without this, a user who reports before acting would be stuck —
   *  no choices block exists yet because the LLM hasn't been asked
   *  for a verdict. Wires to ChatView.sendMessage("Give me a new
   *  spot to drill..."). */
  onNextSpot?: () => void;
}

export function SpotCard({
  spot,
  locked,
  onAction,
  conversationId,
  onNextSpot,
}: SpotCardProps) {
  const [sizing, setSizing] = useState<"idle" | "picking">("idle");
  const [reportOpen, setReportOpen] = useState(false);
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reported, setReported] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);

  const effectiveLocked = locked || reported;

  const facingBet = isFacingBet(spot);
  const showCheck = !facingBet;
  const callLabel = (() => {
    if (!facingBet) return "Call";
    // Show the chips hero actually adds to call (not the bet's to-level),
    // capped at hero's remaining stack, flagged when it's an all-in call.
    const { amount, allIn } = callAmount(spot);
    return allIn
      ? `Call ${formatBb(amount)} (all-in)`
      : `Call ${formatBb(amount)}`;
  })();
  // Label refinement:
  //   preflop, no prior non-Hero raise   → "Open"  (first voluntary raise,
  //                                          incl. raising over limpers)
  //   preflop, prior non-Hero raise      → "Raise" (3-bet / 4-bet)
  //   postflop, facing a bet             → "Raise"
  //   postflop, no facing bet            → "Bet"
  //
  // The "Open" label decision is INDEPENDENT of `isFacingBet`. A non-BB
  // hero preflop is always facing the implicit 1bb BB level, so
  // `isFacingBet` returns true even when there's been no voluntary
  // raise yet — but that doesn't make the action a "Raise". The label
  // tracks "has someone raised?" instead.
  const isPreflop = spot.street === "preflop";
  const hasPriorRaisePreflop =
    isPreflop &&
    spot.history.some(
      (h) => h.actor !== "Hero" && h.action === "raise",
    );
  const raiseLabel = isPreflop
    ? hasPriorRaisePreflop
      ? "Raise"
      : "Open"
    : facingBet
      ? "Raise"
      : "Bet";
  const raiseAction: SpotAction["action"] = facingBet || isPreflop ? "raise" : "bet";
  const isRaise = facingBet || isPreflop;

  function commit(action: SpotAction) {
    setSizing("idle");
    onAction(action);
  }

  async function submitReport(notes: string) {
    if (!conversationId) {
      setReportError("Conversation not ready. Please try again in a moment.");
      return;
    }
    setReportSubmitting(true);
    setReportError(null);
    try {
      await reportLibrarySpot({ spot, conversationId, notes });
      setReported(true);
      setReportOpen(false);
    } catch (err) {
      setReportError(
        err instanceof Error ? err.message : "Failed to submit report",
      );
    } finally {
      setReportSubmitting(false);
    }
  }

  const [layout] = useSpotLayout();

  // Construct the slots once so both branches reference the same nodes.
  // The actions slot is either the live TableActionBar or the
  // post-report footer — same swap in both layouts.
  const actionsSlot: ReactNode = reported ? (
    <ReportedFooter onNextSpot={onNextSpot} />
  ) : (
    <TableActionBar
      spot={spot}
      locked={effectiveLocked}
      sizing={sizing}
      facingBet={facingBet}
      showCheck={showCheck}
      callLabel={callLabel}
      raiseLabel={raiseLabel}
      raiseAction={raiseAction}
      isRaise={isRaise}
      onPickSize={() => setSizing("picking")}
      onCancelSize={() => setSizing("idle")}
      onCommit={commit}
    />
  );

  const topRightSlot: ReactNode = (() => {
    if (reported) {
      return (
        <span className="px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 text-[9px] font-medium uppercase tracking-wider">
          Reported
        </span>
      );
    }
    if (!effectiveLocked && conversationId) {
      return (
        <button
          type="button"
          onClick={() => setReportOpen(true)}
          aria-label="Report this spot as low quality"
          title="Report this spot"
          className="w-10 h-10 rounded-md text-text-muted/50 hover:text-amber-400 hover:bg-amber-500/10 transition flex items-center justify-center"
        >
          <Flag size={14} strokeWidth={ICON_STROKE} />
        </button>
      );
    }
    return null;
  })();

  const reportModal = reportOpen && (
    <ReportSpotModal
      onCancel={() => {
        setReportOpen(false);
        setReportError(null);
      }}
      onSubmit={submitReport}
      submitting={reportSubmitting}
      error={reportError}
    />
  );

  if (layout === "simplified") {
    return (
      <div className="relative">
        <SimplifiedSpotCard
          spot={spot}
          topRight={topRightSlot}
          actions={actionsSlot}
        />
        {reportModal}
      </div>
    );
  }

  // Default: table layout. Top-right slot is absolutely positioned
  // (the table layout uses overlap-on-table positioning); question +
  // history live outside the felt as separate elements.
  return (
    <div className="relative p-4 sm:p-5 space-y-3">
      {topRightSlot && (
        <div className="absolute top-2 right-2 z-10">{topRightSlot}</div>
      )}

      {spot.question && (
        <div className="text-center text-text/90 text-sm px-6 leading-snug">
          {spot.question}
        </div>
      )}

      <PokerTable spot={spot} />

      {actionsSlot}

      <HistoryNote history={historyToSentences(spot.history, spot.street)} />

      {reportModal}
    </div>
  );
}

// ---------------------------------------------------------------------------
// History note — sits below the action buttons. Renders the
// natural-language action narrative grouped by street so multi-street
// context is scannable at a glance. Each street gets its own line with
// a small uppercase street label, then the prose. Empty histories
// render nothing.
// ---------------------------------------------------------------------------

function HistoryNote({ history }: { history: StreetNarrative[] }) {
  if (history.length === 0) return null;
  return (
    <div className="text-center text-text-muted/80 text-xs px-4 pt-2 space-y-0.5">
      {history.map(({ street, text }) => (
        <div key={street}>
          <span className="uppercase tracking-wider text-text-muted/60 not-italic mr-1.5 text-[10px]">
            {street}
          </span>
          <span className="italic">{text}</span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Reported footer + modal — unchanged from the pre-redesign SpotCard.
// ---------------------------------------------------------------------------

function ReportedFooter({ onNextSpot }: { onNextSpot?: () => void }) {
  return (
    <div className="flex flex-col items-center gap-2 pt-3">
      <div className="text-xs text-text-muted/80 italic">
        Spot reported. Thanks for the feedback.
      </div>
      {onNextSpot && (
        <button
          type="button"
          onClick={onNextSpot}
          className="text-sm text-accent hover:text-accent/80 font-medium"
        >
          Next spot →
        </button>
      )}
    </div>
  );
}

interface ReportSpotModalProps {
  onCancel: () => void;
  onSubmit: (notes: string) => void;
  submitting: boolean;
  error: string | null;
}

function ReportSpotModal({
  onCancel,
  onSubmit,
  submitting,
  error,
}: ReportSpotModalProps) {
  const [notes, setNotes] = useState("");

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-md max-h-[90dvh] flex flex-col rounded-xl border border-border bg-surface"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="font-semibold text-text tracking-tight">
                Report this spot
              </div>
              <p className="text-xs text-text-muted mt-1">
                Removes it from the library immediately. Notes optional.
              </p>
            </div>
            <button
              type="button"
              onClick={onCancel}
              aria-label="Cancel"
              className="w-10 h-10 flex items-center justify-center -mr-2 -mt-2 text-text-muted hover:text-text"
            >
              <X size={16} strokeWidth={ICON_STROKE} />
            </button>
          </div>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="What's wrong with this spot? (optional)"
            rows={3}
            disabled={submitting}
            className="w-full resize-none bg-bg border border-border rounded-lg px-3 py-2 text-text placeholder-text-muted focus:outline-none focus:border-accent disabled:opacity-50"
          />
          {error && (
            <div className="text-xs text-danger">{error}</div>
          )}
        </div>
        <div className="flex justify-end gap-2 p-5 pt-2 border-t border-border/40">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="px-4 py-3 rounded-md text-sm text-text-muted hover:text-text transition disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSubmit(notes)}
            disabled={submitting}
            className="px-4 py-3 rounded-md bg-amber-500 text-white text-sm font-medium hover:bg-amber-500/90 transition disabled:opacity-50"
          >
            {submitting ? "Reporting…" : "Submit report"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// isFacingBet — exported for tests (regression-pinned in SpotCard.test.ts).
// Drives the Check-button visibility (`showCheck = !facingBet`), the Call
// label ("Call Nbb" vs "Call"), and parts of the Raise label/action math.
// ---------------------------------------------------------------------------

/** Determine whether hero must respond to an unresolved aggressive action
 *  from a non-hero player — i.e. cannot legally check.
 *
 *  Postflop rule: walk the CURRENT STREET's history backward for the most
 *  recent non-Hero action. Only same-street entries count — a preflop
 *  raise must not propagate forward to make hero "facing" on the flop.
 *    - non-Hero bet/raise → true (facing)
 *    - non-Hero check    → false (action has passed without aggression)
 *    - non-Hero fold/call → keep scanning (folds remove a player from
 *      the pot; calls match a bet but don't undo it for next-to-act)
 *    - no current-street entries → false (hero is first to act on the
 *      new street; preflop raises don't carry forward)
 *
 *  Preflop rule: the BB itself is a forced 1bb level that hero must
 *  match unless they ARE the BB. Limps (action=call against the 1bb
 *  level) don't reset this — they match the existing bet, they don't
 *  cancel it. So:
 *    - hero is not BB → always facing (default true)
 *    - hero is BB AND any non-Hero raise in history → facing
 *    - hero is BB AND no non-Hero raise → BB option, can check
 *
 *  Earlier versions (between commits 855b0ba and 19af498) lost the
 *  preflop default. That caused the "UTG limps, hero in HJ can magically
 *  check" bug — symptoms: Check button appearing when it should be
 *  Call/Raise/Fold only. Tests below pin every flavor.
 */
export function isFacingBet(spot: SpotPayload): boolean {
  if (spot.street === "preflop") {
    if (spot.position !== "BB") return true;
    // Hero is BB. They have the option to check unless someone raised
    // over the BB. Limps (calls) don't constitute a raise.
    return spot.history.some(
      (h) => h.actor !== "Hero" && h.action === "raise",
    );
  }

  // Postflop: only current-street actions matter for the "facing" check.
  const currentStreet = spot.street;
  for (let i = spot.history.length - 1; i >= 0; i--) {
    const h = spot.history[i]!;
    const entryStreet: SpotPayload["street"] = h.street ?? currentStreet;
    if (entryStreet !== currentStreet) continue;
    if (h.actor === "Hero") continue;
    if (h.action === "bet" || h.action === "raise") return true;
    if (h.action === "check") return false;
  }
  return false;
}
